import Footer from '@/components/Footer';
import { ChangeBookingFooterContent } from '@/components/manage/change-trips/ChangeBookingFooterContent';
import JourneyCard from '@/components/purchase/journey/JourneyCard';
import {
  nonTripOffersLoadingSelector,
  outboundOrInboundJourneysLoadingSelector,
} from '@/features/loading/loadingSelectors';
import { useChangeBookingSelection } from '@/hooks/useChangeBookingSelection';
import { useGTMSearchContext } from '@/hooks/useGTMSearchContext';
import { TransText } from '@/i18n/trans/text';
import { useSelector } from '@/store/utils';
import type {
  SearchJourneyOffersDTO,
  SearchOffersDTO,
} from '@/types/dto/agent';
import { breakpoints } from '@/utils/breakpoints';
import { cn } from '@/utils/cn';
import { GTM_EVENT, trackEcommerceEventV2 } from '@/utils/gtm';
import { mapJourneyToGTMItem } from '@/utils/gtmPayloads';
import { getJourneyWarning } from '@/utils/warning';
import { Button, Icons, Typography } from '@packages/webshop-design-system';
import type { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { type FC, type ReactNode, useEffect, useRef } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useLocation, useNavigate } from 'react-router-dom';

interface JourneyListProps extends SearchOffersDTO {
  setSelectedJourney: ActionCreatorWithPayload<SearchJourneyOffersDTO>;
  onContinue: () => void;
  onGoBack?: () => void;
  title: ReactNode;
  journeySelectedEvent: GTM_EVENT;
}

const JourneyList: FC<JourneyListProps> = ({
  journeys,
  warnings,
  onContinue,
  onGoBack,
  setSelectedJourney,
  title,
  journeySelectedEvent,
}) => {
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });
  const location = useLocation();
  const navigate = useNavigate();
  const { hasSelection, selectedOutboundTrip, selectedInboundTrip } =
    useChangeBookingSelection();
  const bookedTrip =
    location.pathname === '/purchase/inbound/journey'
      ? selectedInboundTrip
      : selectedOutboundTrip;
  const outboundOrInboundJourneysLoading = useSelector(
    outboundOrInboundJourneysLoadingSelector
  );
  const nonTripOffersLoading = useSelector(nonTripOffersLoadingSelector);
  const isPending = outboundOrInboundJourneysLoading || nonTripOffersLoading;

  const { totalPassengers, roundTrip } = useGTMSearchContext();
  const lastTrackedJourneys = useRef<typeof journeys>();
  const isInboundList = location.pathname === '/purchase/inbound/journey';

  // Tagging plan (BR-57983): one search-results push per displayed result
  // set, guarded by result-set identity.
  useEffect(() => {
    if (!journeys?.length || lastTrackedJourneys.current === journeys) {
      return;
    }
    lastTrackedJourneys.current = journeys;

    trackEcommerceEventV2(
      isInboundList
        ? GTM_EVENT.INBOUND_JOURNEY_SEARCH_RESULTS
        : GTM_EVENT.OUTBOUND_JOURNEY_SEARCH_RESULTS,
      {
        total_passenger: totalPassengers || undefined,
        round_trip: roundTrip,
        items: journeys.map((journey) =>
          mapJourneyToGTMItem(journey, totalPassengers || 1)
        ),
      }
    );
  }, [journeys, totalPassengers, roundTrip, isInboundList]);
  // In change-booking mode the back action always returns the user to
  // /manage-booking — there's no useful step-back from the journey list
  // (going to /purchase would just re-fire the auto-search and bounce
  // them right back here). Per-step callers' onGoBack is still honoured
  // outside change-booking mode.
  const effectiveOnGoBack = hasSelection
    ? () => navigate('/manage-booking/details')
    : onGoBack;

  return (
    <>
      <div
        className={cn('mx-auto w-full max-w-laptop p-4', {
          ['pb-24']:
            location.pathname === '/purchase/inbound/journey' ||
            Boolean(effectiveOnGoBack),
        })}
      >
        <div className="mx-auto max-w-mobile" data-testid="journey-list">
          {title && (
            <Typography
              variant={isLaptopOrBigger ? 'heading1' : 'heading2'}
              className="mb-4"
              as="h1"
            >
              {title}
            </Typography>
          )}
          <div className="grid grid-cols-[1fr_auto] gap-y-4">
            {journeys?.map((journey, key) => (
              <JourneyCard
                key={key}
                journey={journey}
                bookedTrip={bookedTrip}
                warning={getJourneyWarning(journey, warnings)}
                onContinue={onContinue}
                setSelectedJourney={setSelectedJourney}
                journeySelectedEvent={journeySelectedEvent}
              />
            ))}
          </div>
        </div>
      </div>
      {effectiveOnGoBack && (
        <Footer
          content={hasSelection ? <ChangeBookingFooterContent /> : undefined}
          actionButtons={
            <>
              <Button
                size={isLaptopOrBigger ? 'medium' : 'large'}
                data-testid="footer-back-button"
                className="rounded-lg laptop:w-auto"
                variant="tertiary"
                onClick={effectiveOnGoBack}
              >
                <Icons.arrowLeft />
                <TransText i18nKey="back" />
              </Button>
              <Button
                size={isLaptopOrBigger ? 'medium' : 'large'}
                data-testid="footer-continue-button"
                className="rounded-lg laptop:w-auto"
                onClick={onContinue}
                variant="cta"
                isDisabled
                isPending={isPending}
              >
                <TransText i18nKey="continue" />
                <Icons.arrowRight />
              </Button>
            </>
          }
        />
      )}
    </>
  );
};

export default JourneyList;
